import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { DollarSign, Save, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '@/hooks/useAuth';
import { PricingBulkImport } from '@/components/pricing/PricingBulkImport';

interface PricingSettings {
  id?: string;
  roof_price_per_sq_asphalt: number;
  roof_price_per_sq_metal: number;
  roof_price_per_sq_flat: number;
  roof_tearoff_per_sq: number;
  roof_dump_fee_per_sq: number;
  roof_base_waste_percent: number;
  roof_steep_pitch_extra_percent: number;
  siding_price_per_sqft_vinyl: number;
  siding_price_per_sqft_fiber: number;
  soffit_price_per_sqft: number;
  fascia_price_per_ft: number;
  gutter_5in_price_per_ft: number;
  gutter_6in_price_per_ft: number;
  downspout_price_each: number;
  gutter_guard_price_per_ft: number;
  profit_margin_percent: number;
  overhead_percent: number;
  updated_at?: string;
}

const defaultSettings: PricingSettings = {
  roof_price_per_sq_asphalt: 350,
  roof_price_per_sq_metal: 650,
  roof_price_per_sq_flat: 400,
  roof_tearoff_per_sq: 75,
  roof_dump_fee_per_sq: 25,
  roof_base_waste_percent: 10,
  roof_steep_pitch_extra_percent: 15,
  siding_price_per_sqft_vinyl: 7.5,
  siding_price_per_sqft_fiber: 12,
  soffit_price_per_sqft: 6,
  fascia_price_per_ft: 8,
  gutter_5in_price_per_ft: 8,
  gutter_6in_price_per_ft: 10,
  downspout_price_each: 45,
  gutter_guard_price_per_ft: 14,
  profit_margin_percent: 20,
  overhead_percent: 15,
};

export default function PricingSettings() {
  const [settings, setSettings] = useState<PricingSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && user) {
      loadSettings();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading, user]);

  const loadSettings = async () => {
    try {
      if (!user) return;

      const { data, error } = await supabase
        .from('pricing_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings(data);
      }
    } catch (error: any) {
      console.error('Error loading pricing settings:', error);
      toast.error('Failed to load pricing settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (!user) throw new Error('Not authenticated');

      const settingsData = {
        ...settings,
        user_id: user.id,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('pricing_settings')
        .upsert(settingsData, { onConflict: 'user_id' });

      if (error) throw error;

      toast.success('Pricing settings saved successfully');
      await loadSettings();
    } catch (error: any) {
      console.error('Error saving pricing settings:', error);
      toast.error('Failed to save pricing settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof PricingSettings, value: string) => {
    const numValue = parseFloat(value) || 0;
    setSettings(prev => ({ ...prev, [field]: numValue }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <div>
        <h1 className="text-3xl font-bold">Pricing & Rates</h1>
        <p className="text-muted-foreground mt-1">
          Set your default pricing rates for instant ballpark estimates on measurements.
        </p>
      </div>

      <PricingBulkImport onImported={loadSettings} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Roof Pricing Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Roof Pricing
            </CardTitle>
            <CardDescription>Per square (100 sq ft) pricing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="asphalt">Asphalt Shingles ($/sq)</Label>
              <Input
                id="asphalt"
                type="number"
                step="10"
                value={settings.roof_price_per_sq_asphalt}
                onChange={(e) => handleChange('roof_price_per_sq_asphalt', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="metal">Metal Roof ($/sq)</Label>
              <Input
                id="metal"
                type="number"
                step="10"
                value={settings.roof_price_per_sq_metal}
                onChange={(e) => handleChange('roof_price_per_sq_metal', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="flat">Flat Roof ($/sq)</Label>
              <Input
                id="flat"
                type="number"
                step="10"
                value={settings.roof_price_per_sq_flat}
                onChange={(e) => handleChange('roof_price_per_sq_flat', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="tearoff">Tear-off ($/sq)</Label>
              <Input
                id="tearoff"
                type="number"
                step="5"
                value={settings.roof_tearoff_per_sq}
                onChange={(e) => handleChange('roof_tearoff_per_sq', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="dump">Dump Fee ($/sq)</Label>
              <Input
                id="dump"
                type="number"
                step="5"
                value={settings.roof_dump_fee_per_sq}
                onChange={(e) => handleChange('roof_dump_fee_per_sq', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="waste">Base Waste (%)</Label>
              <Input
                id="waste"
                type="number"
                step="1"
                value={settings.roof_base_waste_percent}
                onChange={(e) => handleChange('roof_base_waste_percent', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="steep">Steep Pitch Extra (%) - if ≥8/12</Label>
              <Input
                id="steep"
                type="number"
                step="1"
                value={settings.roof_steep_pitch_extra_percent}
                onChange={(e) => handleChange('roof_steep_pitch_extra_percent', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Siding & Exterior Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Siding & Exterior
            </CardTitle>
            <CardDescription>Per square foot or linear foot pricing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="vinyl">Vinyl Siding ($/sq ft)</Label>
              <Input
                id="vinyl"
                type="number"
                step="0.5"
                value={settings.siding_price_per_sqft_vinyl}
                onChange={(e) => handleChange('siding_price_per_sqft_vinyl', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="fiber">Fiber/Hardie Siding ($/sq ft)</Label>
              <Input
                id="fiber"
                type="number"
                step="0.5"
                value={settings.siding_price_per_sqft_fiber}
                onChange={(e) => handleChange('siding_price_per_sqft_fiber', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="soffit">Soffit ($/sq ft)</Label>
              <Input
                id="soffit"
                type="number"
                step="0.5"
                value={settings.soffit_price_per_sqft}
                onChange={(e) => handleChange('soffit_price_per_sqft', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="fascia">Fascia ($/linear ft)</Label>
              <Input
                id="fascia"
                type="number"
                step="0.5"
                value={settings.fascia_price_per_ft}
                onChange={(e) => handleChange('fascia_price_per_ft', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Gutters Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Gutters
            </CardTitle>
            <CardDescription>Per linear foot or per unit pricing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="gutter5">5" Gutter ($/linear ft)</Label>
              <Input
                id="gutter5"
                type="number"
                step="0.5"
                value={settings.gutter_5in_price_per_ft}
                onChange={(e) => handleChange('gutter_5in_price_per_ft', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="gutter6">6" Gutter ($/linear ft)</Label>
              <Input
                id="gutter6"
                type="number"
                step="0.5"
                value={settings.gutter_6in_price_per_ft}
                onChange={(e) => handleChange('gutter_6in_price_per_ft', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="downspout">Downspout ($ each)</Label>
              <Input
                id="downspout"
                type="number"
                step="5"
                value={settings.downspout_price_each}
                onChange={(e) => handleChange('downspout_price_each', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="guard">Gutter Guard ($/linear ft)</Label>
              <Input
                id="guard"
                type="number"
                step="0.5"
                value={settings.gutter_guard_price_per_ft}
                onChange={(e) => handleChange('gutter_guard_price_per_ft', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Business Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Business Settings
            </CardTitle>
            <CardDescription>Overhead and profit margins</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="overhead">Overhead (%)</Label>
              <Input
                id="overhead"
                type="number"
                step="1"
                value={settings.overhead_percent}
                onChange={(e) => handleChange('overhead_percent', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Business costs: insurance, tools, vehicles, office, etc.
              </p>
            </div>
            <div>
              <Label htmlFor="profit">Profit Margin (%)</Label>
              <Input
                id="profit"
                type="number"
                step="1"
                value={settings.profit_margin_percent}
                onChange={(e) => handleChange('profit_margin_percent', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Your target profit after all costs
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {settings.updated_at && (
            <span>Last updated: {format(new Date(settings.updated_at), 'PPp')}</span>
          )}
        </div>
        <Button onClick={handleSave} disabled={saving} size="lg">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Rates
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
